import React, { useEffect, useMemo, useState } from 'react'
import { Campaign } from 'redux/reducers/campaigns'
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table'
import { Table, TableContainer } from '../commonUI/Table/styles'
import { Pagination } from '../commonUI/Table/Pagination'
import { useDispatch, useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { Avatar, Box, Flex, Text, Tooltip, useToast } from '@chakra-ui/core'
import LocaleText from 'components/LocaleText'
import { ConsoleBadge } from '../commonUI/ConsoleBadge'
import { DateToLocaleValue } from 'components/ui/DateToLocaleValue'
import { InputWithIcon } from '../commonUI/InputWithIcon'
import { Copy, Eye, Search, Trash } from 'lucide-react'
import { useUserName } from 'hooks/useUserName'
import useAvatar from 'hooks/useAvatar'
import { NoResults } from 'components/CommonComponents/GroupsTree/NoResults'
import { CustomSkeleton } from 'components/Elements'
import useLocaleText from 'components/useLocaleText'
import dayjs from 'dayjs'
import { useCampaignModals } from './CompetitionModalsManagerContext'
import {
  CAMPAIGN_STATUS_TYPES,
  COMPETITION_TYPE_TYPES,
  DEFAULT_CAMPAIGN_TYPE,
  DEFAULT_PARAMETER,
  RANK_BY_TYPES,
} from './utils'
import { AdminDateRangePicker, DateRangeState } from '../commonUI/AdminDatePicker'
import { AppDispatch } from 'config/redux'
import { ControlledSwitch } from 'components/ui/ControlledSwitch'
import { deleteCampaignAction, getCampaignData, updateCampaignStatus } from 'redux/actions/campaigns'
import { ActionMenu } from '../commonUI/ActionMenu'

const getCompetitionTypeColor = (competitionType: number) => {
  const colors: Record<number, { text: string; background: string; border: string }> = {
    0: { text: '#1a365d', background: '#bee3f8', border: '#3182ce' }, // QTY - blue
    1: { text: '#2d5016', background: '#c6f6d5', border: '#38a169' }, // Income - green
    2: { text: '#553c9a', background: '#e6ccff', border: '#805ad5' }, // PERCENTAGE_INCOME - purple
    3: { text: '#c05621', background: '#fed7aa', border: '#dd6b20' }, // PERCENTAGE_QTY - orange
    4: { text: '#744210', background: '#fef0e6', border: '#d69e2e' }, // WEIGHT - yellow
  }
  return colors[competitionType] || { text: '#2d3748', background: '#edf2f7', border: '#a0aec0' }
}

const getRankByColor = (rankBy: number) => {
  const colors: Record<number, { text: string; background: string; border: string }> = {
    0: { text: '#1a365d', background: '#bee3f8', border: '#3182ce' }, // Value - blue
    1: { text: '#2d5016', background: '#c6f6d5', border: '#38a169' }, // Target - green
  }
  return colors[rankBy] || { text: '#2d3748', background: '#edf2f7', border: '#a0aec0' }
}

const getStatusColor = (status: number) => {
  const colors: Record<number, { text: string; background: string; border: string }> = {
    [CAMPAIGN_STATUS_TYPES.ACTIVE]: { text: '#2d5016', background: '#c6f6d5', border: '#38a169' }, // Active - green
    [CAMPAIGN_STATUS_TYPES.DEACTIVE]: { text: '#c53030', background: '#fed7d7', border: '#e53e3e' }, // Deactive - red
  }
  return colors[status] || { text: '#2d3748', background: '#edf2f7', border: '#a0aec0' }
}

const createColumns = () => [
  {
    Header: () => {
      const t_title = useLocaleText('name', true)
      return (
        <Text>
          <LocaleText text={t_title} />
        </Text>
      )
    },
    accessor: 'title',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const title = row.original.title

      return (
        <Tooltip label={title} placement="bottom" hasArrow aria-label="Competition title" zIndex={10000}>
          <Box w="max-content">
            <Text fontSize="sm" fontWeight="bold" isTruncated maxW="300px">
              {title}
            </Text>
          </Box>
        </Tooltip>
      )
    },
  },
  {
    Header: () => {
      const t_competition_type = useLocaleText('competition_type', true)
      return (
        <Text>
          <LocaleText text={t_competition_type} />
        </Text>
      )
    },
    accessor: 'competition_type',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const competitionType = row.original.competition_type
      const typeName = (COMPETITION_TYPE_TYPES as Record<number, string>)[competitionType] || 'Unknown'
      const t_name = useLocaleText(typeName, true)
      const { text, background, border } = getCompetitionTypeColor(competitionType)

      return (
        <ConsoleBadge borderColor={border} bg={background} color={text}>
          {t_name}
        </ConsoleBadge>
      )
    },
  },
  {
    Header: () => {
      const t_rank_by = useLocaleText('t_rank_by', true)
      return (
        <Text>
          <LocaleText text={t_rank_by} />
        </Text>
      )
    },
    accessor: 'rank_by',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const rankBy = row.original.rank_by
      const rankName = (RANK_BY_TYPES as Record<number, string>)[rankBy] || 'Unknown'
      const t_name = useLocaleText(rankName, true)
      const { text, background, border } = getRankByColor(rankBy)

      return (
        <ConsoleBadge borderColor={border} bg={background} color={text}>
          {t_name}
        </ConsoleBadge>
      )
    },
  },
  {
    Header: () => {
      const t_status = useLocaleText('status', true)
      return (
        <Text>
          <LocaleText text={t_status} />
        </Text>
      )
    },
    accessor: 'status',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const dispatch: AppDispatch = useDispatch()
      const { requestConfirmAction } = useCampaignModals()
      const toast = useToast()
      const status = row.original.status
      const statusName = status === CAMPAIGN_STATUS_TYPES.ACTIVE ? 'active' : 'inactive'
      const t_name = useLocaleText(statusName, true)
      const { text, background, border } = getStatusColor(status)
      const isActive = status === CAMPAIGN_STATUS_TYPES.ACTIVE

      const handleStatusChange = async () => {
        try {
          const updatedCampaign = {
            ...row.original,
            status:
              status === CAMPAIGN_STATUS_TYPES.ACTIVE ? CAMPAIGN_STATUS_TYPES.DEACTIVE : CAMPAIGN_STATUS_TYPES.ACTIVE,
          }
          dispatch(updateCampaignStatus({ campaign_id: row.original.oid, status: updatedCampaign.status }))
          toast({
            title: 'Competition status updated',
            description: 'Competition status updated successfully',
            status: 'success',
            duration: 2000,
            position: 'top-right',
          })
        } catch (error) {
          console.log(`updateCompetitionStatus failed due ${error}`)
          toast({
            title: 'Competition status update failed',
            description: 'Competition status update failed',
            status: 'error',
            duration: 2000,
            position: 'top-right',
          })
        }
      }

      const onToggle = () => {
        requestConfirmAction(handleStatusChange)
      }

      return (
        <Flex align="center" justify="flex-start" style={{ columnGap: '10px' }}>
          <ConsoleBadge borderColor={border} bg={background} color={text}>
            {t_name}
          </ConsoleBadge>
          <ControlledSwitch isChecked={isActive} onChange={onToggle} />
        </Flex>
      )
    },
  },
  {
    Header: () => {
      const t_created_by = useLocaleText('created_by', true)
      return (
        <Text>
          <LocaleText text={t_created_by} />
        </Text>
      )
    },
    accessor: 'created_by',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const uid = row.original.created_by
      const fullName = useUserName(uid)
      const avatar = useAvatar(uid)

      return (
        <Flex align="center" style={{ columnGap: '10px' }}>
          <Avatar name={fullName} src={avatar} size="md" />
          <Box>
            <Text maxW="200px" isTruncated fontWeight="medium">
              {fullName}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {uid}
            </Text>
          </Box>
        </Flex>
      )
    },
  },
  {
    Header: () => {
      const t_data_from = useLocaleText('t_data_from', true)
      return (
        <Text>
          <LocaleText text={t_data_from} />
        </Text>
      )
    },
    accessor: 'data_from_ts',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const fromDate = row.original.data_from_ts ? dayjs(row.original.data_from_ts, 'YYYYMMDDHHmmss').toDate() : null

      return <DateToLocaleValue date={fromDate} placeholder="N/A" fontSize="14px" fontWeight={500} />
    },
  },
  {
    Header: () => {
      const t_data_to = useLocaleText('t_data_to', true)
      return (
        <Text>
          <LocaleText text={t_data_to} />
        </Text>
      )
    },
    accessor: 'data_to_ts',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const toDate = row.original.data_to_ts ? dayjs(row.original.data_to_ts, 'YYYYMMDDHHmmss').toDate() : null

      return <DateToLocaleValue date={toDate} placeholder="N/A" fontSize="14px" fontWeight={500} />
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_actions" />
      </Text>
    ),
    accessor: 'actions',
    Cell: ({ row }: { row: { original: Campaign } }) => {
      const dispatch: AppDispatch = useDispatch()
      const toast = useToast()
      const { requestConfirmAction, openOverviewCampaignModal, openCampaignModal } = useCampaignModals()
      const competition = row.original
      const [competitionOidLoading, setCompetitionOidLoading] = useState<string | null>(null)

      const handleCloneCampaign = async () => {
        try {
          setCompetitionOidLoading(competition.oid)
          const competitionData = await dispatch(
            getCampaignData({ campaign_id: competition.oid, return_products_only: true })
          )
          if (!competitionData) {
            toast({
              title: 'Error loading competition data',
              description: 'Failed to load competition data',
              status: 'error',
              duration: 2000,
              position: 'top-right',
            })
          }
          const { products_details, item_type, contexts } = competitionData || {}
          const { store_id } = contexts || {}
          const { item_id } = products_details || {}
          const storeIds =
            competition?.is_store_specific && store_id ? store_id.map((sid: string) => sid.toString()) : []

          const initialData = {
            root_group_id: null,
            campaign_type: DEFAULT_CAMPAIGN_TYPE,
            products_cluster_data: {
              item: item_id || [],
              store_id: storeIds,
              from_ts: '',
              to_ts: '',
              app_from_ts: '',
              app_to_ts: '',
              baseline_ts: '',
              title: competition.title,
              item_type: item_type || '',
              parameter: DEFAULT_PARAMETER,
              external_clusters_params: {
                rank_by: competition.rank_by,
                competition_type: competition.competition_type,
              },
            },
          }

          openCampaignModal({
            initialData,
          })
        } catch (error) {
          console.log(`handleCloneCampaign failed due ${error}`)
          toast({
            title: 'Error cloning competition',
            description: 'Failed to clone competition',
            status: 'error',
            duration: 2000,
            position: 'top-right',
          })
        } finally {
          setCompetitionOidLoading(null)
        }
      }

      const deleteCompetition = () => {
        requestConfirmAction(handleDeleteCompetition)
      }

      const viewCompetition = async () => {
        try {
          setCompetitionOidLoading(competition.oid)
          const competitionData = await dispatch(
            getCampaignData({ campaign_id: competition.oid, return_products_only: true })
          )

          if (competitionData) {
            openOverviewCampaignModal(competition, competitionData)
          } else {
            toast({
              title: 'Error loading competition data',
              description: 'Failed to load competition data',
              status: 'error',
              duration: 2000,
              position: 'top-right',
            })
          }
        } catch (error) {
          console.error('Error loading competition data:', error)
          toast({
            title: 'Error loading competition data',
            description: 'Failed to load competition data',
            status: 'error',
            duration: 2000,
            position: 'top-right',
          })
        } finally {
          setCompetitionOidLoading(null)
        }
      }

      const handleDeleteCompetition = async () => {
        try {
          setCompetitionOidLoading(competition.oid)
          await dispatch(deleteCampaignAction(competition.oid))

          toast({
            title: 'Competition deleted',
            description: 'Competition deleted successfully',
            status: 'success',
            duration: 2000,
            position: 'top-right',
          })
        } catch (error) {
          console.log(`deleteCampaignAction failed due ${error}`)
          toast({
            title: 'Competition deletion failed',
            description: 'Competition deletion failed',
            status: 'error',
            duration: 2000,
            position: 'top-right',
          })
        } finally {
          setCompetitionOidLoading(null)
        }
      }

      const optionsList = [
        { label: 'admin_info', icon: <Eye size={18} />, onClick: viewCompetition, color: 'blue.500' },
        {
          label: 'admin_duplicate',
          icon: <Copy size={18} />,
          onClick: handleCloneCampaign,
          color: 'green.500',
        },
        { label: 'admin_delete_action', icon: <Trash size={18} />, onClick: deleteCompetition, color: 'red.500' },
      ]

      return (
        <Flex w="100%" justifyContent="flex-start">
          <ActionMenu
            isLoading={competitionOidLoading === competition.oid}
            tooltipText="admin_edit_action"
            optionsList={optionsList}
            triggerProps={{ pos: 'relative', top: 0, right: 0 }}
          />
        </Flex>
      )
    },
  },
]

interface CampaignsTableProps {
  campaigns: Campaign[]
  loading: boolean
  dateRange: DateRangeState
  onDateRangeChange: (dateRange: DateRangeState) => void
}

const TableSkeleton = () => {
  const isRtl = useSelector(isRtlSelector)

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            {[
              'title',
              'competition_type',
              'rank_by',
              'status',
              'created_by',
              'data_from',
              'data_to',
              'admin_actions',
            ].map((header, index) => (
              <th key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                <Text>
                  <LocaleText text={header} isLocalUse />
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: 8 }).map((_, cellIndex) => (
                <td key={cellIndex} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <CustomSkeleton
                    height={cellIndex === 0 || cellIndex === 4 ? '40px' : '20px'}
                    width={cellIndex === 0 ? '300px' : cellIndex === 4 ? '80%' : '60%'}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  )
}

export const CampaignsTable = ({ campaigns, loading, dateRange, onDateRangeChange }: CampaignsTableProps) => {
  const columns = useMemo(() => createColumns(), [])
  const isRtl = useSelector(isRtlSelector)
  const t_search = useLocaleText('search')

  const [searchValue, setSearchValue] = useState('')

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: campaigns,
      initialState: {
        pageIndex: 0,
        pageSize: 12,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  useEffect(() => {
    setGlobalFilter(searchValue)
  }, [searchValue, setGlobalFilter])

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        mb={4}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        style={{ gap: '10px' }}
      >
        <AdminDateRangePicker dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
        <Box w="100%" maxW="300px">
          <InputWithIcon
            icon={<Search />}
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            borderRadius="5px"
            isDisabled={loading}
            placeholder={t_search}
          />
        </Box>
      </Flex>

      {loading ? (
        <TableSkeleton />
      ) : page.length > 0 ? (
        <TableContainer style={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column: any, index: number) => (
                    <th {...column.getHeaderProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any, index: number) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell: any, index: number) => (
                      <td {...cell.getCellProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <NoResults />
      )}

      <Pagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </>
  )
}
